/**
 * Audit-Log (Spec 8.1, 5.6).
 *
 * Beantwortet die Frage, die das Excel nicht beantworten kann: „Warum stand
 * hier im Juli 12,58 €?" Jede Betragsänderung, Freigabe, Session-Aktion und
 * jeder IBAN-Zugriff steht hier mit Vorher, Nachher, Urheber und Grund.
 */
import { Fragment, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { dateTime } from "../lib/format";
import { Empty, ErrorBox, PageHeader, Spinner, Stat } from "../components/ui";

interface AuditEntry {
  id: string;
  entityType: string;
  entityId: string;
  action: string;
  actorName: string;
  reason: string | null;
  before: unknown;
  after: unknown;
  createdAt: string;
}

const ACTION_LABELS: Record<string, string> = {
  CREATE: "angelegt",
  UPDATE: "geändert",
  DELETE: "gelöscht",
  IMPORT_COMMIT: "Import übernommen",
  IMPORT_REVERT: "Import zurückgenommen",
  SESSION_CONFIRM: "Session bestätigt",
  SESSION_SET_DATE: "Betriebstag geändert",
  SESSION_SET_ACTS_AS: "Kasse umgewidmet",
  SESSION_SET_POOL: "Topf geändert",
  SESSION_NO_TIP: "ohne Trinkgeld gewertet",
  SESSION_COUNTERPART: "Gegenwert erfasst",
  SESSION_SPLIT: "Session aufgeteilt",
  SESSION_MERGE: "Sessions zusammengeführt",
  SESSION_PAIR: "manuell gepaart",
  SESSION_TIP_OVERRIDE: "Trinkgeld festgesetzt",
  SESSION_AMOUNT_OVERRIDE: "Beträge korrigiert",
  DAY_CALCULATE: "Tag berechnet",
  DAY_APPROVE: "Tag freigegeben",
  DAY_REOPEN: "Tag wieder geöffnet",
  PAYOUT_LOCK: "Periode gesperrt",
  PAYOUT_UNLOCK: "Periode wieder geöffnet",
  PAYOUT_EXPORT: "Export erzeugt",
  PAYOUT_PAID: "als ausgezahlt vermerkt",
  IBAN_READ: "IBAN eingesehen",
  SETTING_UPDATE: "Einstellung geändert",
  LOGIN: "angemeldet",
  LOGIN_FAILED: "Anmeldung fehlgeschlagen",
  LOGOUT: "abgemeldet",
  PASSWORD_CHANGE: "eigenes Passwort geändert",
  PASSWORD_RESET: "Passwort zurückgesetzt",
  TOTP_ENABLE: "zweiten Faktor eingerichtet",
  TOTP_RESET: "zweiten Faktor zurückgesetzt",
  USER_CREATE: "Zugang angelegt",
  USER_UPDATE: "Zugang geändert",
  USER_UNLOCK: "Sperre aufgehoben",
  MICROSOFT_LINK: "Microsoft-Konto zugeordnet",
  MICROSOFT_LINK_REJECT: "Microsoft-Anmeldung abgelehnt",
  MICROSOFT_UNLINK: "Microsoft-Verknüpfung gelöst",
  TOTP_VERIFY: "Code nach Microsoft-Anmeldung bestätigt",
};

const ENTITY_LABELS: Record<string, string> = {
  cash_session: "Kassensession",
  business_day: "Betriebstag",
  hours_entry: "Stundeneintrag",
  import_batch: "Import",
  employee: "Person",
  pool: "Pool",
  workstation: "Kasse",
  work_area: "Arbeitsbereich",
  payout_period: "Auszahlungsperiode",
  app_setting: "Einstellung",
  app_user: "Zugang",
  microsoft_link_request: "Microsoft-Anmeldung",
};

/**
 * Soll sofort auffallen: Zugriffe auf Bankdaten (Spec 8.3) und alles, was
 * Zugänge verändert — wer eine Rolle bekommt, darf danach an die Bankdaten.
 */
const SENSITIVE = new Set([
  "IBAN_READ",
  "PAYOUT_EXPORT",
  "LOGIN_FAILED",
  "PASSWORD_RESET",
  "TOTP_RESET",
  "USER_CREATE",
  "USER_UPDATE",
  "MICROSOFT_LINK",
  "MICROSOFT_UNLINK",
]);

export function AuditLog() {
  const [entityType, setEntityType] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);

  const entries = useQuery({
    queryKey: ["audit-log", entityType],
    queryFn: () =>
      api.get<{ entries: AuditEntry[] }>(
        `/audit-log?limit=300${entityType ? `&entityType=${entityType}` : ""}`,
      ),
  });

  if (entries.isLoading) return <Spinner label="Änderungshistorie wird geladen" />;
  if (entries.error) return <ErrorBox error={entries.error} />;

  const all = entries.data?.entries ?? [];
  const sensitive = all.filter((e) => SENSITIVE.has(e.action)).length;
  const withReason = all.filter((e) => e.reason).length;

  return (
    <>
      <PageHeader label="Audit" title="Änderungshistorie">
        Jede Korrektur, Freigabe und jeder Zugriff auf Bankdaten steht hier mit Urheber und,
        falls angegeben, Grund. In Excel verschwindet eine überschriebene Zelle spurlos, hier
        nicht.
      </PageHeader>

      <div className="mb-6 flex flex-wrap gap-6">
        <Stat label="Einträge" value={String(all.length)} />
        <Stat label="mit Grund" value={String(withReason)} />
        <Stat
          label="Bankdaten berührt"
          value={String(sensitive)}
          tone={sensitive > 0 ? "alert" : "ok"}
        />
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <button
          className={entityType === "" ? "btn-primary" : "btn-ghost"}
          onClick={() => setEntityType("")}
        >
          Alles
        </button>
        {Object.entries(ENTITY_LABELS).map(([value, label]) => (
          <button
            key={value}
            className={entityType === value ? "btn-primary" : "btn-ghost"}
            onClick={() => setEntityType(value)}
          >
            {label}
          </button>
        ))}
      </div>

      {all.length === 0 ? (
        <Empty>Keine Einträge in dieser Auswahl.</Empty>
      ) : (
        <div className="card overflow-hidden">
          <table className="tbl">
            <thead>
              <tr>
                <th className="w-44">Zeitpunkt</th>
                <th className="w-36">Objekt</th>
                <th className="w-52">Vorgang</th>
                <th>Grund</th>
                <th className="w-40">Wer</th>
              </tr>
            </thead>
            <tbody>
              {all.map((e) => {
                const open = expanded === e.id;
                return (
                  <Fragment key={e.id}>
                    <tr
                      className={`cursor-pointer ${SENSITIVE.has(e.action) ? "bg-warn/5" : ""}`}
                      onClick={() => setExpanded(open ? null : e.id)}
                    >
                      <td className="whitespace-nowrap text-muted">{dateTime(e.createdAt)}</td>
                      <td>{ENTITY_LABELS[e.entityType] ?? e.entityType}</td>
                      <td>
                        {SENSITIVE.has(e.action) ? (
                          <span className="chip-warn mr-2">sensibel</span>
                        ) : null}
                        {ACTION_LABELS[e.action] ?? e.action}
                      </td>
                      <td className="max-w-lg truncate text-muted" title={e.reason ?? ""}>
                        {e.reason ?? "–"}
                      </td>
                      <td className="text-muted">{e.actorName}</td>
                    </tr>
                    {open ? (
                      <tr>
                        <td colSpan={5} className="bg-surface">
                          <div className="grid gap-4 py-2 sm:grid-cols-2">
                            <Payload title="Vorher" value={e.before} />
                            <Payload title="Nachher" value={e.after} />
                          </div>
                          <p className="pb-2 font-mono text-[10px] text-muted">
                            Datensatz {ENTITY_LABELS[e.entityType] ?? e.entityType} ·{" "}
                            {e.entityId}
                          </p>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function Payload({ title, value }: { title: string; value: unknown }) {
  if (value === null || value === undefined) {
    return (
      <div>
        <div className="label mb-1">{title}</div>
        <p className="text-xs text-muted">–</p>
      </div>
    );
  }
  return (
    <div>
      <div className="label mb-1">{title}</div>
      <pre className="overflow-x-auto rounded border border-line bg-page p-3 font-mono text-[11px] leading-relaxed">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}
